import {Component, OnInit, Optional, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {LangService} from '../../../../services/language/lang.service';
import {NgOptimizedImage} from '@angular/common';
import {CountryService} from '../../../../services/country/country.service';
import {Country} from '../../../../models/country';
import {LoaderService} from '../../../../services/loader/loader.service';
import {Partner} from '../../../../models/partner/partner';
import {PartnerService} from '../../../../services/partner/partner.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {User} from '../../../../models/user';
import {UserService} from '../../../../services/user/user.service';
import {concatMap, finalize, forkJoin, from, of} from 'rxjs';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ActivatedRoute, Router} from '@angular/router';
import {UploadFile} from '../../../../models/upload-file';
import {UploadFileService} from '../../../../services/upload-file/upload-file.service';
import {PartnerTabComponent} from '../partner-tab/partner-tab.component';
import {PartnerForm} from '../../../../forms/partner-form';
import {CompanyRole, CompanyRoleLoop} from '../../../../constants/company-role';
import {ContactForm} from '../../../../forms/contact-form';
import {PartnerContactService} from '../../../../services/partner/partner-contact.service';
import {ImageService} from '../../../../services/image/image.service';
import {MetaTitle} from '../../../../decorator/meta-title';
import {TranslatePipe} from '@ngx-translate/core';
import {AuthService} from '../../../../services/auth/auth.service';
import {ConstantList} from '../../../../models/constant-list';
import {ComplianceDocumentsComponent} from '../../../layout/compliance-documents/compliance-documents.component';
import {AdvertisingPlatform} from '../../../../models/advertising-platform';
import {AdvertisingPlatformService} from '../../../../services/advertising-platform/advertising-platform.service';
import {PartnerAdvertisingPlatformService} from '../../../../services/partner/partner-advertising-platform.service';
import {PartnerAdvertisingPlatform} from '../../../../models/partner/partner-advertising-platform';
import {AdvertisingPlatformInfoForm} from '../../../../forms/advertising-platform/advertising-platform-info-form';
import {OutbrainInfoForm} from '../../../../forms/advertising-platform/outbrain-info-form';
import {TaboolaInfoForm} from '../../../../forms/advertising-platform/taboola-info-form';
import {MetaAdsInfoForm} from '../../../../forms/advertising-platform/meta-ads-info-form';
import {GoogleAdsInfoForm} from '../../../../forms/advertising-platform/google-ads-info-form';
import {Currency} from '../../../../models/currency';
import {CurrencyService} from '../../../../services/currency/currency.service';
import {PartnerGoogleAdsService} from '../../../../services/partner/partner-google-ads.service';
import {ConfirmDirective} from '../../../../directives/confirm/confirm.directive';
import {IsGrantedDirective} from '../../../../directives/is-granted/is-granted.directive';
import {Language} from '../../../../models/language';
import {PartnerMetaAdsService} from '../../../../services/partner/partner-meta-ads.service';
import {InputSwitchComponent} from '../../../layout/input-switch/input-switch.component';
import {MicrosoftAdsInfoForm} from '../../../../forms/advertising-platform/microsoft-ads-info-form';
import {PartnerMicrosoftAdsService} from '../../../../services/partner/partner-microsoft-ads.service';
import {XAdsInfoForm} from '../../../../forms/advertising-platform/x-ads-info-form';
import {TiktokAdsInfoForm} from '../../../../forms/advertising-platform/tiktok-ads-info-form';
import {PartnerXAdsService} from '../../../../services/partner/partner-x-ads.service';

@Component({
  selector: 'app-account-management',
  imports: [
    ReactiveFormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    TranslatePipe,
    ComplianceDocumentsComponent,
    ConfirmDirective,
    IsGrantedDirective,
    InputSwitchComponent
  ],
  templateUrl: './partner-account-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './partner-account-management.component.scss'
})
@MetaTitle('Account management')
export class PartnerAccountManagementComponent implements OnInit {

  @ViewChild('compliance') complianceComponent!: ComplianceDocumentsComponent;

  CompanyRole = CompanyRole;
  companyRoles: Set<CompanyRole> = CompanyRoleLoop;

  partner!: Partner;
  countries: Country[] = [];
  accountManagers: User[] = [];

  files: { [key: string]: UploadFile | null } = {
    image: null,
    mainContactDocument: null,
    signatoryContactDocument: null
  };

  form: FormGroup = PartnerForm.getFormGroup();
  contacts: Record<CompanyRole, FormGroup> = {
    [CompanyRole.MAIN]: ContactForm.getFormGroup(CompanyRole.MAIN),
    [CompanyRole.SALES]: ContactForm.getFormGroup(CompanyRole.SALES),
    [CompanyRole.SIGNATORY]: ContactForm.getFormGroup(CompanyRole.SIGNATORY),
    [CompanyRole.ACCOUNTING]: ContactForm.getFormGroup(CompanyRole.ACCOUNTING),
    [CompanyRole.TECHNICAL]: ContactForm.getFormGroup(CompanyRole.TECHNICAL),
  };

  advertisingPlatformInfoForm: AdvertisingPlatformInfoForm | null = null;

  languages: Language[] = [];
  trafficSourceList: string[] = [];
  typologyList: string[] = [];
  experienceList: string[] = [];
  verticalList: ConstantList[] = [];
  estimatedMonthlyRevenueList: string[] = [];
  geographicAreaList: string[] = [];
  prospectOriginList: string[] = [];
  companyStatusList: string[] = [];
  partnerStatusList: string[] = [];
  paymentMethodList: string[] = [];
  paymentTermList: string[] = [];
  civilityList: string[] = [];
  currencies: Currency[] = [];
  advertisingPlatformList: AdvertisingPlatform[] = [];
  partnerAdvertisingPlatforms: PartnerAdvertisingPlatform[] = [];
  googleAdsConversionActions: any[] | null = [];
  googleAdsAccountInfos: any | null = null;
  metaAdsAccountInfos: any | null = null;
  microsoftAdsConversionGoals: any[] | null = null;
  xAdsConversionGoals: any[] | null = null;

  constructor(
    protected imageService: ImageService,
    protected langService: LangService,
    private countryService: CountryService,
    private loader: LoaderService,
    private partnerService: PartnerService,
    private partnerContactService: PartnerContactService,
    private partnerAdvertisingPlatformService: PartnerAdvertisingPlatformService,
    private snackbar: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private uploadFileService: UploadFileService,
    private auth: AuthService,
    private advertisingPlatformService: AdvertisingPlatformService,
    private currencyService: CurrencyService,
    private partnerGoogleAdsService: PartnerGoogleAdsService,
    private partnerMetaAdsService: PartnerMetaAdsService,
    private partnerMicrosoftAdsService: PartnerMicrosoftAdsService,
    private partnerXAdsService: PartnerXAdsService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    this.partnerTab.partnerSubject.subscribe((partner: Partner | null) => {
      if (partner) {
        this.partner = partner;

        forkJoin([
          this.userService.findAll({accountManager: true, sort: 'firstname,lastname'}),
          this.countryService.findAll(),
          this.auth.getAsyncConfig(),
          this.advertisingPlatformService.getList(),
          this.currencyService.findAllAvailable(),
          this.partnerAdvertisingPlatformService.findAll({partnerId: this.partner.id}),
          this.langService.getLanguages()
        ]).subscribe(([accountManagers, countries, config, advertisingPlatforms, currencies, partnerAdvertisingPlatforms, languages]) => {
          this.accountManagers = accountManagers;
          this.countries = countries;
          this.currencies = currencies;
          this.trafficSourceList = config?.data?.trafficSourceList;
          this.typologyList = config?.data?.typologyList;
          this.experienceList = config?.data?.experienceList;
          this.verticalList = config?.data?.verticalList.map((vertical: any) => new ConstantList().deserialize(vertical));
          this.estimatedMonthlyRevenueList = config?.data?.estimatedMonthlyRevenueList;
          this.geographicAreaList = config?.data?.geographicAreaList;
          this.prospectOriginList = config?.data?.prospectOriginList;
          this.companyStatusList = config?.data?.companyStatusList;
          this.partnerStatusList = config?.data?.partnerStatusList;
          this.paymentMethodList = config?.data?.paymentMethodList;
          this.paymentTermList = config?.data?.paymentTermList;
          this.civilityList = config?.data?.civilityList;
          this.languages = languages;

          this.advertisingPlatformList = advertisingPlatforms;
          this.partnerAdvertisingPlatforms = partnerAdvertisingPlatforms;

          PartnerForm.update(this.form, this.partner);

          const companyRoles = Object.values(CompanyRole);
          for (const companyRole of companyRoles) {
            if (this.contacts[companyRole] && this.partner.contacts[companyRole]) {
              ContactForm.update(this.contacts[companyRole], this.partner.contacts[companyRole]);
            }
          }

          if (this.partner.advertisingPlatform) {
            this.initAdvertisingPlatformInfoForm(this.partner.advertisingPlatform, this.getPartnerAdvertisingPlatform());
          }

          this.refreshGoogleAdsAccountInfos();
          this.refreshGoogleAdsActionStatus();
          this.refreshMetaAdsAccountInfos();
          this.refreshMicrosoftAdsActionStatus();
          this.refreshXAdsActionStatus();

          this.loader.stopLoading();
        });
      }
    });
  }

  getAdvertisingPlatform(): AdvertisingPlatform {
    if (!this.partner.advertisingPlatform) {
      throw new Error('Advertising platform is not set.');
    }

    return this.partner.advertisingPlatform;
  }

  initAdvertisingPlatformInfoForm(advertisingPlatform: AdvertisingPlatform, partnerAdvertisingPlatform: PartnerAdvertisingPlatform | null): void {
    const form = {
      [GoogleAdsInfoForm.getPlatformName()]: new GoogleAdsInfoForm(),
      [MetaAdsInfoForm.getPlatformName()]: new MetaAdsInfoForm(),
      [OutbrainInfoForm.getPlatformName()]: new OutbrainInfoForm(),
      [TaboolaInfoForm.getPlatformName()]: new TaboolaInfoForm(),
      [MicrosoftAdsInfoForm.getPlatformName()]: new MicrosoftAdsInfoForm(),
      [XAdsInfoForm.getPlatformName()]: new XAdsInfoForm(),
      [TiktokAdsInfoForm.getPlatformName()]: new TiktokAdsInfoForm(),
    }[advertisingPlatform.identifier];

    this.advertisingPlatformInfoForm = form;

    if (form && partnerAdvertisingPlatform) {
      this.advertisingPlatformInfoForm.update(partnerAdvertisingPlatform);
    }
  }

  getPartnerAdvertisingPlatform(): PartnerAdvertisingPlatform | null {
    return this.partnerAdvertisingPlatforms.find((item: PartnerAdvertisingPlatform) => item.isPlatform(this.partner.advertisingPlatform)) || null;
  }

  isLoading(): boolean {
    return this.loader.isLoading();
  }

  documentUpload(event: Event, uploadFile: string): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((data: UploadFile) => {
      this.files[uploadFile] = data;
    });
  }

  onSubmitAll(): void {
    const partnerId: number | null = this.partner.id ?? null;

    if (!this.form.valid || !partnerId) {
      return;
    }

    this.loader.startLoading();

    this.complianceComponent?.sendUploadedFile();

    const roles = Object.values(CompanyRole);

    from([
      this.submitPartner(partnerId),
      ...roles.map((role) => this.submitOneContact(role as CompanyRole)),
      this.submitAdvertisingPlatform()
    ])
      .pipe(
        concatMap((request) => request),
        finalize(() => this.loader.stopLoading())
      )
      .subscribe({
        complete: () => this.fetchPartner('Partner has been updated.')
      });
  }

  private submitPartner(partnerId: number) {
    let params: any = this.form.value;
    params.prospect = false;
    params.paymentThreshold = params.paymentThreshold ? Number(String(params.paymentThreshold).replace(',', '.')) * 100 : null;

    if (this.files['image']) {
      params.image = this.files['image'];
    }

    return this.partnerService.update(partnerId, params);
  }

  private submitAdvertisingPlatform() {
    const advertisingPlatform = this.partner.advertisingPlatform;

    if (!this.advertisingPlatformInfoForm || !advertisingPlatform) {
      return of(undefined);
    }

    const params = {
      currencyId: this.advertisingPlatformInfoForm.getFormGroup().value.currencyId,
      authenticationInfo: this.advertisingPlatformInfoForm.getAuthenticationInfoValues(),
    };

    const partnerAdvertisingPlatform = this.getPartnerAdvertisingPlatform();

    if (partnerAdvertisingPlatform?.id) {
      return this.partnerAdvertisingPlatformService.update(partnerAdvertisingPlatform.id, params);
    }

    const hasValue = Object.values(this.advertisingPlatformInfoForm.getFormGroup().value).some((value) => !!value);
    if (!hasValue) {
      return of(undefined);
    }

    return this.partnerAdvertisingPlatformService.create({
      partnerId: this.partner.id,
      advertisingPlatformId: advertisingPlatform.id,
      ...params,
    });
  }

  isGoogleAds(): boolean {
    const hasCustomerId = !!this.getPartnerAdvertisingPlatform()?.authenticationInfo?.customerId;
    return this.partner?.advertisingPlatform?.identifier === 'google-ads' && hasCustomerId;
  }

  isMetaAds(): boolean {
    const hasAccountId = !!this.getPartnerAdvertisingPlatform()?.authenticationInfo?.accountId;
    return this.partner?.advertisingPlatform?.identifier === 'meta-ads' && hasAccountId;
  }

  isMicrosoftAds(): boolean {
    return this.partner?.advertisingPlatform?.identifier === 'microsoft-ads';
  }

  isXAds(): boolean {
    return this.partner?.advertisingPlatform?.identifier === 'x-ads';
  }

  refreshGoogleAdsActionStatus(): void {
    if (!this.isGoogleAds()) {
      return;
    }

    if (this.partner.id) {
      this.googleAdsConversionActions = null;
      this.partnerGoogleAdsService.getConversionActionsStatus(this.partner.id).subscribe((actions) => {
        this.googleAdsConversionActions = actions;
      });
    }
  }

  refreshGoogleAdsAccountInfos(): void {
    if (!this.isGoogleAds()) {
      return;
    }

    if (this.partner.id) {
      this.googleAdsAccountInfos = null;
      this.partnerGoogleAdsService.getAccountInfos(this.partner.id).subscribe((accountInfos) => {
        this.googleAdsAccountInfos = accountInfos;
      });
    }
  }

  createGoogleAdsActions(): void {
    if (this.partner.id) {
      this.googleAdsConversionActions = null;
      this.partnerGoogleAdsService.createConversionActionsStatus(this.partner.id).subscribe(() => {
        this.refreshGoogleAdsActionStatus();
      });
    }
  }

  refreshMetaAdsAccountInfos(): void {
    if (!this.isMetaAds()) {
      return;
    }

    if (this.partner.id) {
      this.metaAdsAccountInfos = null;
      this.partnerMetaAdsService.getAccountInfos(this.partner.id).subscribe((accountInfos) => {
        this.metaAdsAccountInfos = accountInfos;
      });
    }
  }

  private fetchPartner(message: string | null = null): void {
    if (!this.partner.id) {
      return;
    }

    this.partnerService.findOneById(this.partner.id, {groups: 'contacts,image,documents,document,advertisingPlatforms'}).subscribe(partner => {
      this.partnerTab.partnerSubject.next(partner);

      if (message) {
        this.snackbar.success(message);
      }
    });
  }

  getGoogleAdsTimezoneOffset(): string {
    if (this.googleAdsAccountInfos) {
      let offset = this.googleAdsAccountInfos.timezoneOffset;
      if (offset > 0) {
        return 'UTC+' + offset;
      }

      if (offset < 0) {
        return 'UTC' + offset;
      }

      return 'UTC';
    }

    return '';
  }

  getMetaAdsTimezoneOffset(): string {
    if (this.metaAdsAccountInfos) {
      let offset = this.metaAdsAccountInfos.timezoneOffset;
      if (offset > 0) {
        return 'UTC+' + offset;
      }

      if (offset < 0) {
        return 'UTC' + offset;
      }

      return 'UTC';
    }

    return '';
  }

  private submitOneContact(role: CompanyRole) {
    const form = this.contacts[role];
    if (!form) return of(undefined);

    const params: any = {...form.value};

    switch (role) {
      case CompanyRole.MAIN:
        if (this.files['mainContactDocument']) params.document = this.files['mainContactDocument'];
        break;
      case CompanyRole.SIGNATORY:
        if (this.files['signatoryContactDocument']) params.document = this.files['signatoryContactDocument'];
        break;
    }

    const contact = this.partner.contacts[role];

    if (contact && contact.id) {
      return this.partnerContactService.update(contact.id, params);
    }

    const hasValue = Object.entries(params).some(([key, value]) =>
      key !== 'partnerId' && key !== 'companyRole' && !!value
    );
    if (!hasValue) {
      return of(undefined);
    }

    params.partnerId = this.partner.id;
    return this.partnerContactService.create(params);
  }

  linkToMicrosoftAdvertising(): void {
    if (this.partner.id) {
      this.partnerMicrosoftAdsService.getAuthenticationLink(this.partner.id).subscribe(response => {
        window.location.href = response.link;
      });
    }
  }

  refreshMicrosoftAdsActionStatus(): void {
    if (!this.isMicrosoftAds()) {
      return;
    }

    const authInfo = this.getPartnerAdvertisingPlatform()?.authenticationInfo;

    if (this.partner.id && authInfo && authInfo?.accessToken) {
      this.microsoftAdsConversionGoals = null;
      this.partnerMicrosoftAdsService.getConversionGoalsStatus(this.partner.id).subscribe((goals) => {
        this.microsoftAdsConversionGoals = goals;
      });
    }
  }

  refreshXAdsActionStatus(): void {
    if (!this.isXAds()) {
      return;
    }

    const authInfo = this.getPartnerAdvertisingPlatform()?.authenticationInfo;

    if (this.partner.id && authInfo && authInfo.accountId) {
      this.xAdsConversionGoals = null;
      this.partnerXAdsService.getConversionGoalsStatus(this.partner.id).subscribe((goals) => {
        this.xAdsConversionGoals = goals;
      });
    }
  }
}
